import { devLog } from './devLog';
import type { DrumInstrument, DrumGridData, HitLevel } from '../types';

export interface ParseAsciiResult {
  grid: DrumGridData;
  drumKitId: string | null;
}

export function parseAsciiTxt(text: string, currentInstruments: DrumInstrument[] = []): ParseAsciiResult {
  // 驗證輸入
  if (!text || typeof text !== 'string') {
    throw new Error('Invalid input: text must be a non-empty string');
  }

  const rawLines = text.trim().split('\n')
    .filter(line => line.trim()) // 移除空行
    .filter(line => !line.trim().startsWith('#')); // 移除註釋行
  if (rawLines.length === 0) {
    throw new Error('Invalid input: no valid lines found');
  }

  let drumKitId: string | null = null;
  const kitMatch = rawLines[0].match(/^(?:KIT|DRUMSET|DRUMKIT):\s*(\w+)/i);
  const lines = kitMatch ? rawLines.slice(1) : rawLines;
  if (kitMatch) {
    drumKitId = kitMatch[1];
  }

  const grid: DrumGridData = {};

  // 如果提供了當前樂器列表，使用它們的ID順序
  const availableIds = currentInstruments.length > 0 
    ? currentInstruments.map(inst => inst.id).filter(id => id) // 過濾空ID
    : ["BD", "SD", "CH", "OH", "CP", "RS", "T1", "T2", "T3"]; // 預設樂器ID

  devLog('🎵 ASCII Parser - available IDs:', availableIds);
  devLog('🎵 ASCII Parser - input lines:', lines.length);

  lines.forEach((line, idx) => {
    try {
      // 嘗試解析包含樂器ID的格式：ID: pattern
      const colonMatch = line.match(/^([A-Z0-9]+):\s*(.+)$/);
      
      if (colonMatch) {
        // 格式: "BD: x-x-x-x-"
        const [, instrumentId, pattern] = colonMatch;
        if (pattern) {
          const cleanPattern = pattern.replace(/\|/g, '').trim();
          
          // 直接使用解析出的樂器ID，不管當前鼓組是否有該樂器
          // 這樣可以保留原始的樂器ID對應關係
          grid[instrumentId] = cleanPattern.split('').map((c): HitLevel => {
            if (c === 'x' || c === 'X') return c === 'X' ? 'X' : 'x';
            if (c === 'g') return 'g'; // ghost note
            if (c === 'f') return 'f'; // flam
            return null;
          });
          devLog(`🎵 ASCII Parser - parsed ${instrumentId}: ${cleanPattern}`);
        }
      } else {
        // 簡單格式：每行對應一個樂器（按順序）
        if (idx < availableIds.length) {
          const id = availableIds[idx];
          const cleanPattern = line.replace(/\|/g, '').trim();
          if (cleanPattern) {
            grid[id] = cleanPattern.split('').map((c): HitLevel => {
              if (c === 'x' || c === 'X') return c === 'X' ? 'X' : 'x';
              if (c === 'g') return 'g'; // ghost note
              if (c === 'f') return 'f'; // flam
              return null;
            });
            devLog(`🎵 ASCII Parser - parsed ${id}: ${cleanPattern}`);
          }
        } else {
          console.warn(`🎵 ASCII Parser - line ${idx + 1} exceeds available instruments (${availableIds.length})`);
        }
      }
    } catch (error) {
      console.error(`🎵 ASCII Parser - error parsing line ${idx + 1}:`, error);
      // 繼續處理其他行
    }
  });

  devLog('🎵 ASCII Parser - final grid:', Object.keys(grid));
  
  if (Object.keys(grid).length === 0) {
    throw new Error('No valid patterns found in the file');
  }

  return { grid, drumKitId };
} 